import { useState, useCallback, useEffect, useMemo } from "react";
import { Badge, Flex, Text, Table, TextField } from "@radix-ui/themes";
import { Loader2Icon } from "lucide-react";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { useCustomFieldApi } from "@/v2/entities/custom-field/CustomField.api";
import { CustomField } from "@/v2/entities/custom-field/CustomField.model";
import { CustomFieldItem } from "./CustomFieldItem";
import styles from "./CustomFields.module.scss";

export function CustomFields() {
  const customFieldApi = useCustomFieldApi();
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const fetchCustomFields = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await customFieldApi.getCustomFieldKeys();
      setCustomFields(response || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("failed to fetch custom fields:", error);
      setCustomFields([]);
    } finally {
      setIsLoading(false);
    }
  }, [customFieldApi]);

  useEffect(() => {
    fetchCustomFields();
  }, [fetchCustomFields]);

  const handleToggle = useCallback(
    async (externalId: string, isActive: boolean) => {
      const field = customFields.find((f) => f.externalId === externalId);
      if (!field) return;

      try {
        await customFieldApi.updateCustomField({
          customFieldExternalId: externalId,
          isActive,
          key: field.key,
        });
        setCustomFields((prev) =>
          prev.map((f) =>
            f.externalId === externalId ? { ...f, isActive } : f
          )
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("failed to update custom field:", error);
        throw error;
      }
    },
    [customFieldApi, customFields]
  );

  const filteredCustomFields = useMemo(() => {
    if (searchQuery.length < 2) {
      return customFields;
    }
    const query = searchQuery.toLowerCase();
    return customFields.filter((field) =>
      field.key.toLowerCase().includes(query)
    );
  }, [customFields, searchQuery]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  if (isLoading) {
    return (
      <Flex justify="center" align="center" className={styles.loaderContainer}>
        <Loader2Icon className="animate-spin" />
      </Flex>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text as="div" size="4" weight="medium">
          Custom Fields
        </Text>
      </div>

      <div className={styles.content}>
        <Flex direction="column" gap="8">
          <Flex direction="row" gap="2" align="center" justify="between">
            <Flex gap="2" align="center">
              <Text size="3" weight="medium">
                Custom Fields
              </Text>
              <Badge color="gray">{filteredCustomFields.length}</Badge>
            </Flex>
            <TextField.Root
              placeholder="Search by key..."
              size="2"
              value={searchQuery}
              onChange={handleSearchChange}
              className={styles.searchInput}
            >
              <TextField.Slot>
                <MagnifyingGlassIcon height="16" width="16" />
              </TextField.Slot>
            </TextField.Root>
          </Flex>

          <Table.Root>
            <Table.Header>
              <Table.Row className="text-gray-500 font-normal">
                <Table.ColumnHeaderCell className="font-normal w-1/4">
                  <Text as="span" size="2" color="gray">
                    Name
                  </Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal w-1/4">
                  <Text as="span" size="2" color="gray">
                    External ID
                  </Text>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="font-normal w-1/4">
                  <Text as="span" size="2" color="gray">
                    Active
                  </Text>
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredCustomFields.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={3}>
                    <Flex
                      align="center"
                      justify="center"
                      py="6"
                      direction="column"
                      gap="2"
                    >
                      <Text size="2" color="gray">
                        {searchQuery.length >= 2
                          ? "No custom fields found matching your search"
                          : "No custom fields found"}
                      </Text>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              )}
              {filteredCustomFields
                .slice()
                .sort((a: CustomField, b: CustomField) => {
                  return a.key.toLowerCase().localeCompare(b.key.toLowerCase());
                })
                .map((field: CustomField) => (
                  <CustomFieldItem
                    key={field.externalId}
                    customField={field}
                    onToggle={handleToggle}
                  />
                ))}
            </Table.Body>
          </Table.Root>
        </Flex>
      </div>
    </div>
  );
}
